// Batched, cached city → {lat, lng} resolver via Mapbox Forward Geocoding.
//
// Why this exists: many legacy/seeded events were created before the
// coordinates picker shipped (Phase 2 / v8). They have a `city` string but
// no `latitude`/`longitude`, so radius filtering can't measure their
// distance. Instead of dropping them, we geocode their city on the fly,
// compute haversine distance against the user's centre, and let the radius
// pill do its job. Results are cached in sessionStorage so a feed scroll
// triggers at most one Mapbox call per unique city per session.
//
// Mapbox proximity bias keeps "Anand" → Anand (Gujarat, IN) instead of a
// random Buddhist landmark elsewhere when we already know roughly where
// the user is.
import { MAPBOX_TOKEN } from "@/lib/mapbox";

const STORAGE_KEY = "inonit:cityCoords";
const memCache = new Map(); // lowercase city → {lat,lng} | null  (null = known miss)
let hydrated = false;

function hydrate() {
  if (hydrated) return;
  hydrated = true;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const obj = JSON.parse(raw);
    for (const [k, v] of Object.entries(obj || {})) memCache.set(k, v);
  } catch {}
}

function persist() {
  try {
    const obj = {};
    memCache.forEach((v, k) => { obj[k] = v; });
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
  } catch {}
}

async function geocodeOne(city, proximity) {
  if (!MAPBOX_TOKEN) return null;
  const params = new URLSearchParams({
    access_token: MAPBOX_TOKEN,
    autocomplete: "false",
    limit: "1",
    types: "place,locality,district",
  });
  if (proximity && Number.isFinite(proximity.lat) && Number.isFinite(proximity.lng)) {
    params.set("proximity", `${proximity.lng},${proximity.lat}`);
  }
  const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(city)}.json?${params.toString()}`;
  try {
    const r = await fetch(url);
    if (!r.ok) return null;
    const d = await r.json();
    const f = d.features?.[0];
    if (!f?.center) return null;
    return { lat: f.center[1], lng: f.center[0] };
  } catch {
    return null;
  }
}

/**
 * Resolve a list of city names to coordinates in parallel.
 * @param {string[]} cityNames
 * @param {{lat:number,lng:number}=} proximity
 * @returns {Promise<Map<string, {lat:number,lng:number}>>}
 */
export async function geocodeCities(cityNames, proximity) {
  hydrate();
  const out = new Map();
  const todo = [];
  const seen = new Set();
  for (const raw of cityNames || []) {
    const key = String(raw || "").trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    if (memCache.has(key)) {
      const v = memCache.get(key);
      if (v) out.set(key, v);
    } else {
      todo.push(key);
    }
  }
  if (todo.length) {
    const results = await Promise.all(todo.map((k) => geocodeOne(k, proximity)));
    let dirty = false;
    todo.forEach((k, i) => {
      const v = results[i];
      if (v) {
        memCache.set(k, v);
        out.set(k, v);
        dirty = true;
      }
      // Intentionally NOT caching null misses — a transient Mapbox failure
      // for a popular city (e.g. the user's own profile city) shouldn't
      // become sticky for the whole session. Worst case: one extra fetch
      // on the next render. Hits are still cached aggressively.
    });
    if (dirty) persist();
  }
  return out;
}
